import { PhoneNumberUtil } from 'google-libphonenumber';
import { head, reduce, tail } from 'lodash';

import { DEFAULT_COUNTRY_DATA, DEFAULT_PHONE_FORMAT } from '~common';
import { allCountries, type CountryItem, guessSelectedCountry } from '~configs';
import { TPhoneNumberCountryData } from '~types';

export const isValidPhoneNumber = (
  rawPhoneNumber: string,
  countryData?: TPhoneNumberCountryData,
) => {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parseAndKeepRawInput(
      rawPhoneNumber,
      countryData?.countryCode,
    );

    return phoneUtil.isValidNumber(number);
  } catch (e) {
    return false;
  }
};

export const parseRegionCodeFromRawPhoneNumber = (rawPhoneNumber: string) => {
  if (!isValidPhoneNumber(rawPhoneNumber)) {
    return null;
  }

  const phoneUtil = PhoneNumberUtil.getInstance();
  const parsedPhoneNumber = phoneUtil.parseAndKeepRawInput(rawPhoneNumber);
  const countryCode = parsedPhoneNumber.getCountryCode();

  if (!countryCode) {
    return null;
  }
  return phoneUtil.getRegionCodeForCountryCode(countryCode);
};

export const parseCountryCodeFromRawPhoneNumber = (rawPhoneNumber: string) => {
  if (!isValidPhoneNumber(rawPhoneNumber)) {
    return null;
  }
  const phoneUtil = PhoneNumberUtil.getInstance();

  const parsedPhoneNumber = phoneUtil.parseAndKeepRawInput(rawPhoneNumber);
  return parsedPhoneNumber.getCountryCode();
};

export const formatNumber = (
  text: string | undefined,
  disableCountryCode = false,
  enableLongNumbers = false,
  autoFormat = true,
) => {
  if (!text || text.length === 0) {
    return '';
  }

  const country = guessSelectedCountry(
    text.replace(/\D/g, '').substring(0, 6),
    allCountries,
    DEFAULT_COUNTRY_DATA.dialCode,
  );

  const patternArg = country.format;

  let pattern;
  if (disableCountryCode && patternArg) {
    pattern = patternArg.split(' ');
    pattern.shift();
    pattern = pattern.join(' ');
  } else {
    pattern = patternArg;
  }

  // for all strings with length less than 3, just return it (1, 2 etc.)
  // also return the same text if the selected country has no fixed format
  if ((text && text.length < 2) || !pattern || !autoFormat) {
    return disableCountryCode ? text : `+${text}`;
  }

  const formattedObject = reduce(
    pattern,
    (acc, character) => {
      if (acc.remainingText.length === 0) {
        return acc;
      }

      if (character !== '.') {
        return {
          formattedText: acc.formattedText + character,
          remainingText: acc.remainingText,
        };
      }

      return {
        formattedText: acc.formattedText + head(acc.remainingText),
        remainingText: tail(acc.remainingText),
      };
    },
    {
      formattedText: '',
      remainingText: text.split(''),
    },
  );

  let formattedNumber;
  if (enableLongNumbers) {
    formattedNumber =
      formattedObject.formattedText + formattedObject.remainingText.join('');
  } else {
    formattedNumber = formattedObject.formattedText;
  }
  // Always close brackets
  if (formattedNumber.includes('(') && !formattedNumber.includes(')'))
    formattedNumber += ')';
  return formattedNumber;
};

// This function is aim to convert format into phone number without country code
export const convertToNationalMask = (country: CountryItem) => {
  if (!country.format) return DEFAULT_PHONE_FORMAT;
  return country.format
    .split(' ')
    .slice(country.isAreaCode ? 2 : 1) // For case that country code included areaCode
    .join(' ');
};

export const formatPhoneBaseOnPattern = (input: string, pattern: string) => {
  const tempText = input.replace(/[^0-9]/g, '');
  if (!tempText.length) return '';
  let y = 0;
  let finalText = '';
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '.') {
      finalText += tempText[y];
      y++;
    } else finalText += pattern[i];
    if (y === tempText.length) i = pattern.length;
  }
  return finalText;
};

// Get national number from raw phone number, ex: '+84 921 213 213' -> '921 213 213'
// It work only incase that rawPhoneNumber have correct format
export const getNationalNumber = (rawPhoneNumber: string) => {
  return rawPhoneNumber.split(' ').slice(1).join(' ') || '';
};

// It work only incase that rawPhoneNumber have correct format
export const getCountry = (rawPhoneNumber: string) => {
  let countryCode = rawPhoneNumber.split(' ')[0];
  if (countryCode[0] === '+') {
    countryCode = countryCode.substring(1);
  }
  const format = rawPhoneNumber
    .replace(/[0-9]/g, '.')
    .split(' ')
    .slice(1)
    .join(' ');

  return (
    allCountries.find(
      (item) =>
        item.dialCode === countryCode &&
        convertToNationalMask(item).includes(format), //Compare both country code and format
    ) || DEFAULT_COUNTRY_DATA //Return default data if can't find any
  );
};
